import type { ReactNode } from 'react';
import type { ResourceState } from '../hooks/useResource';
import { Icon } from './Icon';

export function EmptyState({ title, children, action, glyph = 'inbox', tone = '' }: { title: string; children?: ReactNode; action?: ReactNode; glyph?: 'inbox' | 'lock' | 'alert' | 'IM'; tone?: '' | 'amber' | 'danger' }) {
  return (
    <div className="empty-panel" role="status">
      <div>
        <div className={`empty-glyph ${tone}`}>{glyph === 'IM' ? 'IM' : <Icon name={glyph} />}</div>
        <h2>{title}</h2>
        {children && <p>{children}</p>}
        {action}
      </div>
    </div>
  );
}

export function LoadingState({ variant = 'block' }: { variant?: 'metrics' | 'block' | 'grid' }) {
  return (
    <div aria-busy="true" aria-live="polite">
      <span className="sr-only">Carregando…</span>
      {variant === 'metrics' ? <div className="skeleton-grid">{[0, 1, 2, 3].map((i) => <div key={i} className="skeleton" />)}</div> : null}
      {variant === 'grid' ? <div className="skeleton-grid">{[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="skeleton" style={{ height: 180 }} />)}</div> : null}
      <div className="skeleton skeleton-block" />
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: Error; onRetry: () => void }) {
  const correlation = (error as { correlationId?: string }).correlationId;
  return (
    <EmptyState title="Não foi possível carregar" glyph="alert" tone="danger"
      action={<button className="secondary" onClick={onRetry}>Tentar novamente</button>}>
      {error.message}{correlation ? ` Código: ${correlation}.` : ''}
    </EmptyState>
  );
}

export function ForbiddenState() {
  return (
    <EmptyState title="Sem permissão para esta área" glyph="lock" tone="amber">
      Seu papel nesta organização não permite visualizar este conteúdo. Fale com um administrador da organização.
    </EmptyState>
  );
}

/** Renderiza os estados obrigatórios (IM-UI-006) de forma uniforme. */
export function AsyncView<T>({ state, onRetry, empty, loading = 'block', children }: {
  state: ResourceState<T>; onRetry: () => void; empty: ReactNode; loading?: 'metrics' | 'block' | 'grid'; children: (data: T) => ReactNode;
}) {
  switch (state.kind) {
    case 'loading': return <LoadingState variant={loading} />;
    case 'error': return <ErrorState error={state.error} onRetry={onRetry} />;
    case 'forbidden': return <ForbiddenState />;
    case 'empty': return <>{empty}</>;
    case 'ready': return <>{children(state.data)}</>;
  }
}
